/**
 * Where a "Sign in with Twasta" comes back to, on an application's own domain.
 *
 * The platform has redirected here with a one-time code. This page does one
 * thing with it: hand it to the server, together with the `state` this tab
 * started the sign-in with. The server checks that the code was minted for
 * this host, for this handshake, seconds ago and not spent — and answers by
 * setting the session cookie for this origin.
 *
 * Then it leaves, with a full navigation rather than a route change: the whole
 * point of the exchange is a cookie the app did not have when it loaded, and a
 * reload is the honest way to pick it up. The code is never kept, and the URL
 * that carried it is replaced.
 */

import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { exchangeSsoCode, startTwastaSso, takeSsoState } from '@/api/sso'
import { getHostBinding } from '@/config/hostBinding'

export default function SsoCallback() {
  const [error, setError] = useState<string | null>(null)
  // React runs effects twice in StrictMode, and a code is single-use: the
  // second run would spend a code that is already gone and report a failure
  // over a sign-in that worked.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const params = new URLSearchParams(window.location.search)
    const code = params.get('code') || ''
    const returned = params.get('state') || ''
    const expected = takeSsoState()

    // Drop the code out of the address bar before anything else — it is spent
    // either way, and it has no business in the history or a referrer.
    window.history.replaceState({}, '', window.location.pathname)

    if (!code || !returned) {
      setError('This sign-in link is incomplete.')
      return
    }
    if (!expected || expected !== returned) {
      // The sign-in did not start in this tab. That is either a stale tab or
      // somebody else's link, and neither should be allowed to sign anyone in.
      setError('This sign-in did not start here. Please try again.')
      return
    }

    exchangeSsoCode(code, expected)
      .then((res) => {
        window.location.replace(res.redirect_path || '/')
      })
      .catch((err) => {
        const detail = (err as { response?: { data?: { detail?: unknown } } })
          ?.response?.data?.detail
        setError(typeof detail === 'string' && detail
          ? detail
          : 'Sign-in could not be completed.')
      })
  }, [])

  const binding = getHostBinding()

  return (
    <div style={{
      height: '100vh', width: '100vw', background: 'var(--color-bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      {error ? (
        <div style={{ maxWidth: 380, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
            Could not sign you in
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            {error}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 }}>
            {binding && (
              <button
                onClick={() => startTwastaSso(binding, '/')}
                style={btnStyle}
              >
                Try again
              </button>
            )}
            <button onClick={() => window.location.replace('/')} style={btnStyle}>
              Back to the app
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
            Signing you in…
          </span>
        </div>
      )}
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '6px 14px', fontSize: 13, borderRadius: 6,
  border: '1px solid var(--color-border)', background: 'var(--color-surface)',
  color: 'var(--color-text)', cursor: 'pointer',
}
